import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axios";

const SkeletonLoader = () => {
  return (
    <div className="w-full flex flex-col items-center animate-pulse">
      {/* Blog Image */}
      <div className="w-11/12 max-w-3xl h-64 bg-gray-300 mt-6 rounded-lg"></div>

      {/* Blog Heading */}
      <div className="w-11/12 max-w-2xl mt-6 space-y-4">
        <div className="h-6 bg-gray-300 rounded w-3/4"></div>
        <div className="h-4 bg-gray-300 rounded w-1/2"></div>
      </div>

      {/* Blog Content */}
      <div className="w-11/12 max-w-3xl mt-6 space-y-3">
        <div className="h-4 bg-gray-300 rounded"></div>
        <div className="h-4 bg-gray-300 rounded w-5/6"></div>
        <div className="h-4 bg-gray-300 rounded w-4/6"></div>
        <div className="h-4 bg-gray-300 rounded w-2/3"></div>
      </div>
    </div>
  );
};

const AdminBlogDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`/blogs/blog-by-id/${id}`);
        setBlog(response.data);
        setError(false);
      } catch (err) {
        console.error("Error fetching admin blog details:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchBlog();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-start justify-center py-10">
        <SkeletonLoader />
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center px-4">
          <h3 className="text-red-600 font-semibold text-2xl mb-2">
            Blog Not Found
          </h3>
          <p className="text-gray-500 mb-6 text-sm max-w-md mx-auto">
            The blog you’re looking for doesn’t exist or has been removed.
          </p>
          <button
            onClick={() => navigate("/admin/blogs")}
            className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-lg shadow-md transition-colors duration-200"
          >
            Back to Admin Blogs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center bg-gray-50 pb-12">
      {/* Blog Image */}
      <div className="w-11/12 max-w-3xl">
        <img
          src={blog.images?.[0]}
          alt={blog.title}
          className="w-full h-96 object-cover rounded-lg shadow-md mt-6"
        />
      </div>

      {/* Blog Title + Meta */}
      <div className="w-11/12 max-w-3xl mt-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">{blog.title}</h1>
        <div className="text-sm text-gray-500 flex items-center gap-4">
          <span className="bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
            {blog.publishedDate?.slice(0, 10)}
          </span>
          <span className="text-gray-700">By {blog.author || "Admin"}</span>
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              blog.status === "published"
                ? "bg-green-100 text-green-600"
                : blog.status === "draft"
                ? "bg-yellow-100 text-yellow-600"
                : "bg-gray-200 text-gray-600"
            }`}
          >
            {blog.status}
          </span>
        </div>
      </div>

      {/* Blog Content */}
      <div className="w-11/12 max-w-3xl mt-6 text-gray-700 leading-relaxed space-y-4">
        <p>{blog.content}</p>
      </div>
    </div>
  );
};

export default AdminBlogDetails;
